import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { IconClose, IconCheckmark } from '../../components';

import css from './MockupUploadBanner.module.css';

// Playful messages to cycle through during processing
// Note: Dots (...) are animated via CSS, so not included here
const PROCESSING_MESSAGES = [
  'Processing your image',
  'This might take a minute',
  'You can continue browsing meanwhile',  
  'Creating your preview',
  'Finding the perfect spot',
  'Teaching walls to smile',
  'Helping the art pick its favorite spot', 
  'Adjusting for lighting and layout',
  'Your wall is about to meet its match.', 
  'We\'ll let you know when it\'s ready',
  'Matching scale and lighting', 
  'The art is getting comfortable', 
  'Making it pixel perfect.',
  'Just a moment',
  'Adding the finishing touches.',
];

const MockupUploadBanner = props => {
  const {
    isUploading,
    progressStatus,
    error,
    listingContext,
    onViewListing,
    onDismiss,
    className,
    rootClassName,
  } = props;

  // State for cycling through processing messages
  const [messageIndex, setMessageIndex] = useState(0);

  // Cycle through messages every 5 seconds while uploading/processing
  const isProcessing = isUploading && ['pending', 'ai-processing', 'uploading', 'segmenting'].includes(progressStatus);

  useEffect(() => {
    if (isProcessing) {
      const intervalId = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % PROCESSING_MESSAGES.length);
      }, 5000);

      return () => clearInterval(intervalId);
    } else {
      // Reset to first message when not uploading
      setMessageIndex(0);
    }
  }, [isProcessing]);

  const classes = classNames(rootClassName || css.root, className);

  // Don't render if no active upload
  if (!progressStatus) {
    return null;
  }

  const isCompleted = progressStatus === 'completed';
  const isFailed = progressStatus === 'failed';

  // Progress messages
  const getProgressMessage = () => {
    if (isFailed) {
      return error || 'Upload failed';
    }
    if (isCompleted) {
      return 'Your mockup is ready!';
    }
    // Show cycling message during upload/processing
    return PROCESSING_MESSAGES[messageIndex];
  };

  return (
    <div className={classes}>
      <div className={css.content}>
        {/* Icon/Status Indicator */}
        <div className={css.iconContainer}>
          {isUploading && (
            <img
              src="/static/images/mockupIcon.png"
              alt=""
              className={css.mockupIconSpinner}
            />
          )}
          {isCompleted && (
            <div className={css.successIcon}>
              ✨
            </div>
          )}
          {isFailed && (
            <div className={css.errorIcon}>
              ⚠️
            </div>
          )}
        </div>

        {/* Message */}
        <div className={css.message}>
          <span
            key={isProcessing ? messageIndex : 'static'}
            className={`${css.messageText} ${isProcessing ? css.animatedDots : ''}`}
          >
            {getProgressMessage()}
          </span>
          {listingContext && (
            <span className={css.listingTitle}>
              {listingContext.listingTitle}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className={css.actions}>
          {isCompleted && (
            <button
              type="button"
              className={css.viewButton}
              onClick={onViewListing}
            >
              View Mockup
            </button>
          )}
          {(isCompleted || isFailed) && (
            <button
              type="button"
              className={css.dismissButton}
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <IconClose size="small" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

MockupUploadBanner.defaultProps = {
  className: null,
  rootClassName: null,
  error: null,
  listingContext: null,
};

MockupUploadBanner.propTypes = {
  isUploading: PropTypes.bool.isRequired,
  progressStatus: PropTypes.string,
  error: PropTypes.string,
  listingContext: PropTypes.shape({
    listingId: PropTypes.string.isRequired,
    listingSlug: PropTypes.string.isRequired,
    listingTitle: PropTypes.string.isRequired,
  }),
  onViewListing: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default MockupUploadBanner;
